import React, { useState } from 'react';
import { X, Camera, Star, MapPin, Award, CheckCircle, CreditCard, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import '../../../styles/bookingWidget.css';

const MobileBookingSidebar = ({
  isOpen,
  onClose,
  onOpen,
  vendor,
  category,
  service,
  serviceRating,
  serviceReviews,
  bookingData,
  calculateSubtotal,
  calculateCommission,
  calculateTotal,
  calculateDepositAmount,
  calculatePaymentTerms,
  commissionSetting,
  appliedDiscount,
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const hasTerms = vendor?.terms_conditions && vendor.terms_conditions.trim().length > 0;
  const paymentTerms = calculatePaymentTerms?.();

  return (
    <>
      {/* Floating Button - Only show when modal is closed */}
      {!isOpen && (
        <div className="lg:hidden fixed bottom-6 right-6 z-40">
          <button
            onClick={onOpen}
            className="booking-float-button bg-black text-white rounded-full p-4 shadow-lg hover:bg-gray-900 transition-colors relative group"
          >
            <CreditCard className="w-6 h-6 relative z-10" />
            {/* Pulse ring effect */}
            <span className="absolute inset-0 rounded-full bg-black opacity-30 booking-ping-animation" />
          </button>
        </div>
      )}

      {/* Modal */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex items-end"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal Content */}
          <div
            className="relative w-full bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between z-10">
            <h3 className="font-bold text-lg text-black">Booking Summary</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-black" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-6">
            {/* Vendor Info */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mr-3">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-black">
                    {vendor?.business_name || 'Vendor'}
                  </h3>
                  <div className="flex items-center text-xs text-gray-600 mt-1">
                    <Star className="w-3.5 h-3.5 text-black fill-current mr-1" />
                    <span className="font-semibold text-black">
                      {typeof serviceRating === 'number' && !isNaN(serviceRating)
                        ? serviceRating.toFixed(1)
                        : '0.0'}
                    </span>
                    <span className="text-gray-500 ml-1">({serviceReviews})</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex items-center">
                  <MapPin className="w-3.5 h-3.5 mr-2 text-black" />
                  <span>
                    {vendor
                      ? `${vendor.city}, ${vendor.state}`
                      : 'Location not available'}
                  </span>
                </div>
                <div className="flex items-center">
                  <Award className="w-3.5 h-3.5 mr-2 text-black" />
                  <span>{category?.category_name || 'Service Category'}</span>
                </div>
                {vendor?.is_verified && (
                  <div className="flex items-center">
                    <CheckCircle className="w-3.5 h-3.5 mr-2 text-black" />
                    <span className="text-black font-semibold">Verified Vendor</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t-2 border-gray-200">
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-gray-100 rounded-lg p-2.5">
                    <div className="text-base font-bold text-black">
                      ${service?.base_price || '0'}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">Starting Price</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-2.5">
                    <div className="text-base font-bold text-black">
                      {service?.min_duration_hours
                        ? `${service.min_duration_hours}-${
                            service.max_duration_hours || service.min_duration_hours
                          }h`
                        : 'Flexible'}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">Duration</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            {bookingData.selectedPackage && (
              <div className="bg-white border border-black rounded-lg p-4">
                <h3 className="font-bold text-base mb-4 text-black">Booking Summary</h3>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center py-2 border-b border-gray-300">
                    <span className="text-xs text-gray-600 font-medium">Package:</span>
                    <span className="text-xs font-bold text-black">
                      {bookingData.selectedPackage.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-300">
                    <span className="text-xs text-gray-600 font-medium">Base Price:</span>
                    <span className="text-xs font-bold text-black">
                      ${Number(bookingData.selectedPackage.price || 0).toFixed(2)}
                    </span>
                  </div>
                  {parseInt(bookingData.duration) >
                    parseInt(bookingData.selectedPackage.duration_hours || 4) && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-300">
                      <span className="text-xs text-gray-600 font-medium">
                        Extra Hours:
                      </span>
                      <span className="text-xs font-bold text-black">
                        ${(
                          (parseInt(bookingData.duration) -
                            parseInt(
                              bookingData.selectedPackage.duration_hours || 4
                            )) *
                          50
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {appliedDiscount && appliedDiscount.discount_amount > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-300">
                      <span className="text-xs text-gray-600 font-medium">
                        Discount ({appliedDiscount.code}):
                      </span>
                      <span className="text-xs font-bold text-green-600">
                        -${Number(appliedDiscount.discount_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {commissionSetting &&
                    commissionSetting.is_active &&
                    calculateCommission() > 0 && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-300">
                        <span className="text-xs text-gray-600 font-medium">
                          Platform Fee:
                        </span>
                        <span className="text-xs font-bold text-black">
                          ${calculateCommission().toFixed(2)}
                          {commissionSetting.commission_type === 'percentage' && (
                            <span className="text-xs text-gray-500 ml-1">
                              ({commissionSetting.commission_rate}%)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  <div className="border-t-2 border-gray-300 pt-3 mt-3">
                    <div className="flex justify-between items-center mb-2.5">
                      <span className="text-xs text-gray-600 font-medium">
                        Deposit {paymentTerms ? `(${paymentTerms.depositLabel})` : '(50%)'}:
                      </span>
                      <span className="text-xs font-bold text-black">
                        ${calculateDepositAmount().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center bg-black rounded-lg p-3">
                      <span className="font-bold text-sm text-white">Amount Due Now:</span>
                      <span className="font-bold text-xl text-white">
                        ${calculateDepositAmount().toFixed(2)}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500 text-center">
                      Full Total: ${calculateTotal().toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms & Conditions Section */}
            {/* {hasTerms && (
              <div className="border border-gray-200 rounded-lg p-4">
                <button
                  onClick={() => setShowTerms(!showTerms)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-black mr-2" />
                    <h4 className="font-semibold text-xs text-black">
                      Terms & Conditions
                    </h4>
                  </div>
                  {showTerms ? (
                    <ChevronUp className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                {showTerms && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                      {vendor.terms_conditions}
                    </div>
                  </div>
                )}
              </div>
            )} */}
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default MobileBookingSidebar;

